/* eslint-disable max-len */
/* eslint-disable no-underscore-dangle */
/* eslint-disable import/no-unresolved */

import { VideoMedia } from 'theme';

class ResponsiveVideoMedia extends VideoMedia {
  #responsiveVideoData = null;

  #currentVideoType = null;

  #mustRemoveControlsAfterSuspend = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('responsive-video')) {
      this._setupResponsiveVideo();
    }
  }

  disconnectedCallback() {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }
    super.disconnectedCallback();
  }

  _setupResponsiveVideo() {
    const { sectionId } = this.dataset;

    if (!sectionId) {
      console.warn('Responsive video missing section ID');
      return;
    }

    try {
      const scriptElement = document.getElementById(`responsive-video-data-${sectionId}`);

      if (!scriptElement) {
        console.warn('Responsive video script element not found');
        return;
      }

      const videoData = JSON.parse(scriptElement.textContent);

      if (!videoData.mobile || !videoData.desktop) {
        console.warn('Responsive video data missing mobile or desktop video');
        return;
      }

      this.#responsiveVideoData = videoData;

      this._loadResponsiveVideo();

      this._resizeHandler = ResponsiveVideoMedia._debounce(this._handleResize.bind(this), 250);
      window.addEventListener('resize', this._resizeHandler);

      const originalDisconnectedCallback = this.disconnectedCallback;
      this.disconnectedCallback = () => {
        window.removeEventListener('resize', this._resizeHandler);
        if (originalDisconnectedCallback) {
          originalDisconnectedCallback.call(this);
        }
      };
    } catch (error) {
      console.error('Error parsing responsive video data:', error);
    }
  }

  _loadResponsiveVideo() {
    if (!this.#responsiveVideoData) return;

    const isMobile = window.innerWidth <= 699;
    const videoData = isMobile ? this.#responsiveVideoData.mobile : this.#responsiveVideoData.desktop;
    const videoType = isMobile ? 'mobile' : 'desktop';

    if (this.#currentVideoType === videoType) return;

    this.#currentVideoType = videoType;
    this._loadVideoFromData(videoData);
  }

  _loadVideoFromData(videoData) {
    this.innerHTML = '';
    const mediaType = videoData.media_type;
    this.setAttribute('type', mediaType);

    if (videoData.host) {
      this.setAttribute('host', videoData.host);
    }

    if (videoData.aspect_ratio) {
      this.style.setProperty('--aspect-ratio', videoData.aspect_ratio);
    }

    if (mediaType === 'video' || mediaType === 'embed_video') {
      const video = document.createElement('video');

      switch (mediaType) {
        case 'video':
          video.src = videoData.sources[0].url;
          break;
        case 'embed_video':
          video.src = videoData.source;
          break;
        default:
          video.src = '';
      }

      video.muted = this.hasAttribute('autoplay');
      video.loop = true;
      video.controls = this.hasAttribute('controls');
      video.playsInline = true;
      video.autoplay = this.hasAttribute('autoplay');
      video.preload = this.hasAttribute('autoplay') ? 'metadata' : 'none';

      this.appendChild(video);
      this.setAttribute('loaded', '');

      video.addEventListener('play', () => {
        this.setAttribute('playing', '');
        this.removeAttribute('suspended');
        if (this.#mustRemoveControlsAfterSuspend) {
          video.controls = false;
        }
      });

      video.addEventListener('pause', () => {
        if (!video.seeking && video.paused) {
          this.removeAttribute('playing');
        }
      });

      if (this.hasAttribute('autoplay')) {
        setTimeout(() => {
          video.play().catch(() => {
            this.setAttribute('show-play-button', '');
          });
        }, 100);
      }
    } else if (mediaType === 'external_video') {
      this._loadExternalVideo(videoData);
    }
  }

  _loadExternalVideo(videoData) {
    const iframe = document.createElement('iframe');
    const shouldAutoplay = this.hasAttribute('autoplay') && this.hasAttribute('playing');

    if (videoData.external_id) {
      if (videoData.host === 'youtube') {
        iframe.src = `https://www.youtube.com/embed/${videoData.external_id}${shouldAutoplay ? '?autoplay=1&mute=1' : ''}`;
      } else if (videoData.host === 'vimeo') {
        iframe.src = `https://player.vimeo.com/video/${videoData.external_id}${shouldAutoplay ? '?autoplay=1&muted=1' : ''}`;
      }
    }

    iframe.allowFullscreen = true;
    this.appendChild(iframe);
  }

  _handleResize() {
    if (this.#responsiveVideoData) {
      this._loadResponsiveVideo();
    }
  }

  static _debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

if (!window.customElements.get('responsive-video-media')) {
  window.customElements.define('responsive-video-media', ResponsiveVideoMedia);
}

window.ResponsiveVideoMedia = ResponsiveVideoMedia;
